import React, { useState, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Paper,
  ToggleButtonGroup, ToggleButton, Chip, CircularProgress,
  Tab, Tabs, Checkbox, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SchoolIcon from '@mui/icons-material/School';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';

// ─── Constants ───────────────────────────────────────────────

const GOLD = '#d97706';
const GOLD_LIGHT = '#f59e0b';
const BLUE = '#3b82f6';

const getScoreColor = (score) => {
  if (score <= 40) return '#ef4444';
  if (score <= 70) return '#f59e0b';
  return '#10b981';
};

const PRIORITY_COLORS = {
  high: { bg: '#fee2e2', border: '#ef4444', text: '#991b1b', label: 'Hoog' },
  medium: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', label: 'Gemiddeld' },
  low: { bg: '#d1fae5', border: '#10b981', text: '#065f46', label: 'Laag' },
};

// ─── Tip Card Component ──────────────────────────────────────

const TipCard = ({ icon, title, children, color = GOLD }) => (
  <Paper sx={{
    p: 2.5, borderTop: `3px solid ${color}`, height: '100%', minHeight: 140,
    transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 3 },
  }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <span style={{ fontSize: '1.3rem' }}>{icon}</span> {title}
    </Typography>
    <Box sx={{ '& li': { mb: 0.5 }, '& ul': { pl: 2.5, m: 0 } }}>
      {children}
    </Box>
  </Paper>
);

// ─── Learn Tab ───────────────────────────────────────────────

const LearnTab = () => {
  const [checklist, setChecklist] = useState(Array(12).fill(false));

  const toggleCheck = (idx) => {
    setChecklist(prev => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  return (
    <Box>
      {/* Compact hero */}
      <Paper sx={{
        px: 3, py: 2, mb: 3,
        background: 'linear-gradient(135deg, #78350f 0%, #d97706 50%, #f59e0b 100%)',
        color: '#fff', borderRadius: 2,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      }}>
        <Box sx={{ flex: 1, minWidth: 250 }}>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Learnings uit 323 Effie-winnende campagnes — van emotionele kernen tot kanaalstrategieën.
          </Typography>
        </Box>
        <Chip
          label="323 cases geanalyseerd"
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
            fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(255,255,255,0.3)',
          }}
        />
      </Paper>

      {/* De 5 Gouden Regels */}
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmojiEventsIcon sx={{ color: GOLD }} /> De 5 Gouden Regels
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        De fundamenten die terugkomen in vrijwel elke Effie-winnende campagne.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 5 }}>
        {[
          {
            icon: '❤️', title: '1. Emotie wint van ratio', color: '#ef4444',
            desc: 'Verkoop een gevoel, niet een product. Emotie zorgt voor herinnering en actie.',
          },
          {
            icon: '💡', title: '2. Simpele waarheid', color: '#3b82f6',
            desc: 'Start met een confronterend, herkenbaar inzicht. "Iedereen kent iemand die..." werkt beter dan data.',
          },
          {
            icon: '🎯', title: '3. Eén woord, één idee', color: '#8b5cf6',
            desc: 'In maximaal 3 woorden samen te vatten. #DOESLIEF. Ommetje. Whamster.',
          },
          {
            icon: '🌊', title: '4. Cultuur kapen', color: '#10b981',
            desc: 'Haak aan bij bestaande trends of gesprekken. Stap in een bestaande wereld.',
          },
          {
            icon: '🔄', title: '5. Gedrag veranderen', color: GOLD,
            desc: 'Meet niet in likes maar in ander gedrag: meer lopen, minder pesten, anders kopen.',
          },
        ].map(rule => (
          <TipCard key={rule.title} icon={rule.icon} title={rule.title} color={rule.color}>
            <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{rule.desc}</Typography>
          </TipCard>
        ))}
      </Box>

      {/* Bewezen Creatieve Formules */}
      <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 4 }} />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Bewezen Creatieve Formules</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Vijf terugkerende patronen die Effie-jury's keer op keer overtuigen.
      </Typography>
      <Box sx={{ mb: 5 }}>
        {[
          {
            name: 'Confrontatie + Oplossing',
            subtitle: 'Maatschappelijke campagnes',
            color: '#ef4444',
            examples: 'SIRE #DOESLIEF, Ommetje (Hersenstichting)',
            desc: 'Confronteer het publiek met een ongemakkelijke waarheid of gedrag, en bied vervolgens een positieve, haalbare oplossing. Niet wijzen, maar uitnodigen. De kracht zit in de herkenning: "dit doe ik ook".',
          },
          {
            name: 'Underdog verslaat de Reus',
            subtitle: 'Challenger brands',
            color: '#3b82f6',
            examples: 'Hertog Jan vs. Heineken, Zeeman Sneaker',
            desc: 'Een klein merk neemt het op tegen de gevestigde orde — en wint. Dit werkt omdat mensen van nature sympathie voelen voor de underdog. De truc: maak het verschil in kwaliteit of authenticiteit tastbaar.',
          },
          {
            name: 'Nostalgie met een Twist',
            subtitle: 'Emotionele merkcampagnes',
            color: '#8b5cf6',
            examples: 'Albert Heijn Whamster, Evert45',
            desc: 'Roep een herinnering op die iedereen deelt, en geef er een onverwachte, actuele draai aan. Nostalgie verlaagt de weerstand en opent het hart. De twist zorgt voor relevantie en deelbaarheid.',
          },
          {
            name: 'Maak het Persoonlijk',
            subtitle: 'Personalisatie & herkenning',
            color: '#10b981',
            examples: 'McDonald\'s "Ik ben toch zeker...?", Kruidvat met Lies',
            desc: 'Maak de consument de held van het verhaal. Geen abstracte doelgroep, maar één herkenbaar persoon met een herkenbaar moment. Dit werkt extra goed in video: close-ups, echte mensen, echte emotie.',
          },
          {
            name: 'Community als Campagne',
            subtitle: 'Langetermijn engagement',
            color: GOLD,
            examples: 'Hertog Jan biergemeenschap, Plus Knikkeren',
            desc: 'Bouw een gemeenschap rond je merk in plaats van alleen zenden. Laat het publiek meedoen, meepraten, mee-eigenen. De campagne wordt van hen — dat is het sterkste bereik dat er is.',
          },
        ].map(formula => (
          <Accordion key={formula.name} sx={{ mb: 1, '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8fafc', borderLeft: `4px solid ${formula.color}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700}>{formula.name}</Typography>
                <Chip label={formula.subtitle} size="small" sx={{ fontWeight: 600, bgcolor: formula.color, color: '#fff', fontSize: '0.7rem' }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 1.5 }}>{formula.desc}</Typography>
              <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1.5, borderLeft: `3px solid ${formula.color}` }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">Voorbeelden:</Typography>
                <Typography variant="body2" fontWeight={500}>{formula.examples}</Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Kanaalstrategie */}
      <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 4 }} />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Kanaalstrategie</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Welke kanaalcombinatie werkt het beste per campagnedoel?
      </Typography>
      <Paper sx={{ p: 2.5, mb: 5, overflowX: 'auto' }}>
        <Box component="table" sx={{
          width: '100%', borderCollapse: 'collapse',
          '& th, & td': { p: 1.5, textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: '0.85rem' },
          '& th': { fontWeight: 700, bgcolor: '#f8fafc', whiteSpace: 'nowrap' },
        }}>
          <thead>
            <tr>
              <th>Doel</th>
              <th style={{ color: '#10b981' }}>Primair</th>
              <th style={{ color: '#3b82f6' }}>Secundair</th>
              <th style={{ color: '#8b5cf6' }}>Tertiair</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Merkbekendheid breed', 'TV', 'OOH', 'Social'],
              ['Jongeren bereiken', 'Social / YouTube', 'Influencer', 'Experiential'],
              ['Gedragsverandering', 'Experiential', 'Social', 'TV'],
              ['Maatschappelijke impact', 'PR / Earned', 'Social', 'TV'],
              ['Seizoensgebonden sales', 'TV', 'Social', 'In-store'],
            ].map(([doel, primair, secundair, tertiair], i) => (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{doel}</td>
                <td>
                  <Chip label={primair} size="small" sx={{ fontWeight: 600, bgcolor: '#d1fae5', color: '#065f46', fontSize: '0.75rem' }} />
                </td>
                <td>
                  <Chip label={secundair} size="small" sx={{ fontWeight: 600, bgcolor: '#e0f2fe', color: '#0c4a6e', fontSize: '0.75rem' }} />
                </td>
                <td>
                  <Chip label={tertiair} size="small" sx={{ fontWeight: 600, bgcolor: '#f3e8ff', color: '#5b21b6', fontSize: '0.75rem' }} />
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Paper>

      {/* Video Productie Tips */}
      <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 4 }} />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Video Productie Tips</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Directe toepassing voor video-content op basis van Effie-winnaars.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 5 }}>
        {[
          {
            icon: '⚡', title: 'Begin met conflict', color: '#ef4444',
            desc: 'Open nooit met context of uitleg. Start met een spanning, dilemma of confrontatie. Conflict trekt aandacht en houdt vast.',
          },
          {
            icon: '👁️', title: 'Show don\'t tell', color: '#3b82f6',
            desc: 'Laat het zien in plaats van het te vertellen. Eén beeld van een volle prullenbak zegt meer dan tien zinnen over verspilling.',
          },
          {
            icon: '🎭', title: 'Eén emotie per video', color: '#8b5cf6',
            desc: 'Kies één dominante emotie en bouw alles daaromheen. Verwarring of trots of angst — niet alles tegelijk. Focus versterkt impact.',
          },
          {
            icon: '🪝', title: 'Hook in eerste 3 seconden', color: GOLD,
            desc: 'De eerste 3 seconden bepalen of iemand blijft kijken. Geen logo\'s, geen introtitels. Direct actie, emotie of een onverwacht beeld.',
          },
          {
            icon: '🎵', title: 'Muziek als emotioneel anker', color: '#10b981',
            desc: 'Muziek stuurt de emotie meer dan tekst. Kies de track voordat je de edit maakt. Laat de muziek de emotionele boog dragen.',
          },
          {
            icon: '📢', title: 'Eindig met call-to-action', color: '#06b6d4',
            desc: 'Geef de kijker iets concreets om te doen. Niet "denk erover na" maar "download de app", "doe mee", "deel dit". Gedrag veranderen begint met een eerste stap.',
          },
        ].map(tip => (
          <TipCard key={tip.title} icon={tip.icon} title={tip.title} color={tip.color}>
            <Typography variant="body2">{tip.desc}</Typography>
          </TipCard>
        ))}
      </Box>

      {/* Top 10 Cases */}
      <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 4 }} />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Top 10 Cases om te Bestuderen</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        De meest leerzame Effie-cases voor videomakers en strategen.
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 5 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Campagne</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Merk</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Formule</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Waarom bestuderen?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ['1', '#DOESLIEF', 'SIRE', 'Confrontatie + Oplossing', 'Perfecte balans tussen confrontatie en positiviteit, enorme organische spreiding'],
              ['2', 'Ommetje', 'Hersenstichting', 'Confrontatie + Oplossing', 'Gedragsverandering via app, meetbaar resultaat, simpel concept'],
              ['3', 'Whamster', 'Albert Heijn', 'Nostalgie + Twist', 'Cultureel fenomeen gecreëerd, enorme earned media, cross-generatie'],
              ['4', 'Zeeman Sneaker', 'Zeeman', 'Underdog vs. Reus', 'Minimaal budget, maximaal effect, perfecte PR-stunt'],
              ['5', 'Evert45', 'KPN', 'Nostalgie + Twist', 'Emotionele storytelling, maatschappelijke relevantie, viral zonder mediadruk'],
              ['6', 'Hertog Jan', 'Hertog Jan', 'Underdog + Community', 'Langetermijn merkbouw via authenticiteit, community-driven'],
              ['7', 'Even Apeldoorn Bellen', 'Centraal Beheer', 'Herkenning + Humor', 'Decennialang consistent format, iconisch geworden'],
              ['8', 'Knikkeren', 'Plus', 'Community als Campagne', 'Supermarktactie werd nationaal fenomeen, verbond generaties'],
              ['9', 'Open je Wereld', 'Heineken', 'Confrontatie + Dialoog', 'Politiek gevoelig onderwerp, moedig en effectief, internationaal succes'],
              ['10', 'De Kleine Consultant', 'McKinsey / JINC', 'Maak het Persoonlijk', 'Onverwacht perspectief, kinderen als spiegel voor volwassenen'],
            ].map(([num, campagne, merk, formule, waarom]) => (
              <TableRow key={num} sx={{ '&:hover': { bgcolor: '#fefce8' } }}>
                <TableCell>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: '50%', bgcolor: GOLD,
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '0.7rem',
                  }}>
                    {num}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{campagne}</TableCell>
                <TableCell>{merk}</TableCell>
                <TableCell>
                  <Chip label={formule} size="small" sx={{ fontWeight: 500, fontSize: '0.7rem' }} />
                </TableCell>
                <TableCell sx={{ fontSize: '0.82rem' }}>{waarom}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Checklist */}
      <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 4 }} />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Effie Checklist</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Loop deze 12 punten na voor je campagne. Hoe meer je afvinkt, hoe sterker je kans op een effectieve campagne.
      </Typography>
      <Paper sx={{ p: 2.5, mb: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0 }}>
          {[
            'Scherp, confronterend consumenteninzicht als basis',
            'Eén helder, simpel kernidee (in 3 woorden samen te vatten)',
            'Sterke emotionele kern — gevoel gaat boven ratio',
            'Duidelijke gedragsverandering als doel gedefinieerd',
            'Aansluiting bij bestaande cultuur, trends of gesprekken',
            'Kanaalstrategie afgestemd op doel (niet alleen "social")',
            'Video opent met conflict, spanning of verrassing (geen uitleg)',
            'Eén dominante emotie per video gekozen',
            'Concrete call-to-action die gedrag in gang zet',
            'Meetbare KPI\'s gekoppeld aan gedrag, niet alleen bereik',
            'Consistentie in uitvoering over alle touchpoints',
            'Ruimte voor earned media en organische verspreiding',
          ].map((item, i) => (
            <FormControlLabel
              key={i}
              control={
                <Checkbox
                  checked={checklist[i]}
                  onChange={() => toggleCheck(i)}
                  size="small"
                  sx={{
                    color: GOLD,
                    '&.Mui-checked': { color: '#10b981' },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: checklist[i] ? 'line-through' : 'none',
                    color: checklist[i] ? 'text.disabled' : 'text.primary',
                  }}
                >
                  {item}
                </Typography>
              }
              sx={{ mx: 0, py: 0.25 }}
            />
          ))}
        </Box>
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {checklist.filter(Boolean).length} / 12 afgevinkt
          </Typography>
          {checklist.filter(Boolean).length === 12 && (
            <Chip
              icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
              label="Klaar voor een Effie!"
              size="small"
              sx={{ bgcolor: '#fef3c7', color: GOLD, fontWeight: 700 }}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

// ─── Analyze Tab ─────────────────────────────────────────────

const AnalyzeTab = () => {
  const [inputText, setInputText] = useState('');
  const [analysisType, setAnalysisType] = useState('brief');
  const [model, setModel] = useState('gpt');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setInputText(ev.target.result);
      toast.success(`${file.name} geladen`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleAnalyze = async () => {
    if (!inputText.trim()) {
      toast.error('Voer eerst een tekst in');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/effie/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: inputText,
          analysis_type: analysisType,
          model: model,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || err.error || 'Analyse mislukt');
      }
      const data = await resp.json();
      setResult(data);
      toast.success('Analyse voltooid!');
    } catch (e) {
      toast.error(e.message || 'Fout bij analyseren');
    } finally {
      setLoading(false);
    }
  };

  const copyResults = () => {
    if (!result) return;
    const lines = [
      `Effie Score: ${result.score}/100 — ${result.verdict}`,
      '',
      '--- Sterke punten ---',
      ...(result.strengths || []).map(s => `+ ${s.rule}: ${s.explanation}`),
      '',
      '--- Verbeterpunten ---',
      ...(result.improvements || []).map(i => `- [${i.priority}] ${i.rule}: ${i.suggestion}`),
      '',
      `Formule Match: ${result.formula_match?.name} (${result.formula_match?.match_percentage}%)`,
      result.formula_match?.explanation || '',
      '',
      '--- Kanaaladvies ---',
      ...(result.channel_advice || []).map(c => `${c.channel}: ${c.recommendation}`),
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    toast.success('Resultaten gekopieerd');
  };

  return (
    <Box>
      {/* Input mode */}
      {!result && (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Text input */}
          <Box sx={{ flex: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{
              p: 3, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Tekst</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".txt,.pdf,.docx"
                    onChange={handleFileUpload}
                  />
                  <Button
                    size="small"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ textTransform: 'none' }}
                  >
                    Upload
                  </Button>
                </Box>
              </Box>
              <TextField
                multiline
                minRows={12}
                maxRows={28}
                fullWidth
                placeholder="Plak hier je campagne-briefing, script, of conceptomschrijving..."
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.7,
                    bgcolor: '#fafbfc',
                    borderRadius: 1.5,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e5e7eb',
                  },
                }}
              />
            </Paper>
          </Box>

          {/* Parameters */}
          <Box sx={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Analysis Type */}
            <Paper sx={{
              p: 2.5, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
            }}>
              <Typography variant="overline" sx={{
                color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem',
                letterSpacing: '0.08em', display: 'block', mb: 0.5,
              }}>
                Type analyse
              </Typography>
              <ToggleButtonGroup
                value={analysisType}
                exclusive
                onChange={(_, v) => v && setAnalysisType(v)}
                size="small"
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none', fontWeight: 600, py: 1, fontSize: '0.82rem',
                  },
                  '& .Mui-selected': {
                    bgcolor: '#fef3c7 !important',
                    color: '#92400e !important',
                  },
                }}
              >
                <ToggleButton value="brief">Campagne Brief</ToggleButton>
                <ToggleButton value="script">Video Script</ToggleButton>
                <ToggleButton value="concept">Concept</ToggleButton>
              </ToggleButtonGroup>
            </Paper>

            {/* Model Selection */}
            <Paper sx={{
              p: 2.5, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
            }}>
              <Typography variant="overline" sx={{
                color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem',
                letterSpacing: '0.08em', display: 'block', mb: 0.5,
              }}>
                AI Model
              </Typography>
              <ToggleButtonGroup
                value={model}
                exclusive
                onChange={(_, v) => v && setModel(v)}
                size="small"
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none', fontWeight: 600, gap: 0.5, py: 1,
                  },
                  '& .Mui-selected': {
                    bgcolor: model === 'gpt' ? '#e8f5e9 !important' : '#f3e8ff !important',
                    color: model === 'gpt' ? '#2e7d32 !important' : '#7c3aed !important',
                  },
                }}
              >
                <ToggleButton value="gpt">
                  <SmartToyIcon sx={{ fontSize: 18 }} />
                  GPT
                </ToggleButton>
                <ToggleButton value="claude">
                  <PsychologyIcon sx={{ fontSize: 18 }} />
                  Claude
                </ToggleButton>
              </ToggleButtonGroup>
            </Paper>

            {/* Info box */}
            <Paper sx={{
              p: 2, borderRadius: 2, bgcolor: '#fffbeb',
              border: '1px solid #fde68a',
            }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <LightbulbIcon sx={{ color: GOLD, fontSize: 20, mt: 0.2 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#78350f' }}>
                  De AI analyseert je tekst tegen de learnings uit 323 Effie-winnende campagnes en geeft een score, sterke punten, verbeterpunten en kanaaladvies.
                </Typography>
              </Box>
            </Paper>

            {/* Analyze Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              onClick={handleAnalyze}
              disabled={loading || !inputText.trim()}
              sx={{
                py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '0.95rem',
                textTransform: 'none',
                background: loading
                  ? undefined
                  : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
                boxShadow: loading ? undefined : '0 4px 14px 0 rgba(217, 119, 6, 0.35)',
                '&:hover': {
                  background: `linear-gradient(135deg, #b45309 0%, ${GOLD} 100%)`,
                  boxShadow: '0 6px 20px 0 rgba(217, 119, 6, 0.45)',
                },
                '&.Mui-disabled': {
                  background: '#e5e7eb',
                },
              }}
            >
              {loading ? 'Bezig met analyseren...' : 'Analyseren'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Results */}
      {result && (
        <Box>
          {/* Top bar */}
          <Paper sx={{
            p: 2, mb: 3, borderRadius: 2,
            border: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setResult(null)}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              Terug naar invoer
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={copyResults}
              sx={{ textTransform: 'none' }}
            >
              Kopieer resultaten
            </Button>
          </Paper>

          {/* Score + Verdict */}
          <Paper sx={{
            p: 3, mb: 3, borderRadius: 2,
            border: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
          }}>
            {/* Score circle */}
            <Box sx={{
              position: 'relative', display: 'inline-flex',
              flexShrink: 0,
            }}>
              <CircularProgress
                variant="determinate"
                value={result.score || 0}
                size={100}
                thickness={5}
                sx={{
                  color: getScoreColor(result.score),
                  '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                }}
              />
              <CircularProgress
                variant="determinate"
                value={100}
                size={100}
                thickness={5}
                sx={{
                  color: '#f3f4f6',
                  position: 'absolute', left: 0, top: 0,
                  '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                  zIndex: -1,
                }}
              />
              <Box sx={{
                position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column',
              }}>
                <Typography variant="h4" fontWeight={800} sx={{ color: getScoreColor(result.score), lineHeight: 1 }}>
                  {result.score}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  / 100
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                {result.verdict || 'Analyse voltooid'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {result.score <= 40 && 'Dit concept heeft fundamentele verbeterpunten. Zie de aanbevelingen hieronder.'}
                {result.score > 40 && result.score <= 70 && 'Een goed begin met duidelijke kansen voor versterking.'}
                {result.score > 70 && 'Sterk concept dat goed aansluit bij Effie-winnende campagnes.'}
              </Typography>
            </Box>
          </Paper>

          {/* Strengths */}
          {result.strengths?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircleIcon sx={{ color: '#10b981' }} /> Sterke punten
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                {result.strengths.map((s, i) => (
                  <Paper key={i} sx={{
                    p: 2, borderRadius: 1.5,
                    borderLeft: '4px solid #10b981', bgcolor: '#f0fdf4',
                  }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#065f46', mb: 0.5 }}>
                      {s.rule}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {s.explanation}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Improvements */}
          {result.improvements?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WarningIcon sx={{ color: '#f59e0b' }} /> Verbeterpunten
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[...result.improvements]
                  .sort((a, b) => {
                    const order = { high: 0, medium: 1, low: 2 };
                    return (order[a.priority] ?? 1) - (order[b.priority] ?? 1);
                  })
                  .map((imp, i) => {
                    const prio = PRIORITY_COLORS[imp.priority] || PRIORITY_COLORS.medium;
                    return (
                      <Paper key={i} sx={{
                        p: 2, borderRadius: 1.5,
                        borderLeft: `4px solid ${prio.border}`, bgcolor: prio.bg,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: prio.text }}>
                            {imp.rule}
                          </Typography>
                          <Chip
                            label={prio.label}
                            size="small"
                            sx={{
                              fontWeight: 700, fontSize: '0.65rem', height: 20,
                              bgcolor: prio.border, color: '#fff',
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {imp.suggestion}
                        </Typography>
                      </Paper>
                    );
                  })}
              </Box>
            </Box>
          )}

          {/* Formula Match */}
          {result.formula_match && (
            <Paper sx={{
              p: 2.5, mb: 3, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            }}>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <EmojiEventsIcon sx={{ color: GOLD }} /> Formule Match
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#78350f' }}>
                  {result.formula_match.name}
                </Typography>
                <Chip
                  label={`${result.formula_match.match_percentage}% match`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: getScoreColor(result.formula_match.match_percentage),
                    color: '#fff',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#78350f' }}>
                {result.formula_match.explanation}
              </Typography>
            </Paper>
          )}

          {/* Channel Advice */}
          {result.channel_advice?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Kanaaladvies
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                {result.channel_advice.map((ch, i) => (
                  <Paper key={i} sx={{
                    p: 2, borderRadius: 1.5,
                    borderTop: `3px solid ${BLUE}`,
                    transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 3 },
                  }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {ch.channel}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                      {ch.recommendation}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Effie Checklist from analysis */}
          {result.effie_checklist?.length > 0 && (
            <Paper sx={{
              p: 2.5, mb: 3, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
            }}>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmojiEventsIcon sx={{ color: GOLD }} /> Effie Checklist
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {result.effie_checklist.map((item, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                    p: 1.5, borderRadius: 1.5,
                    bgcolor: item.present ? '#f0fdf4' : '#fef2f2',
                    borderLeft: `3px solid ${item.present ? '#10b981' : '#ef4444'}`,
                  }}>
                    {item.present ? (
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20, mt: 0.1 }} />
                    ) : (
                      <WarningIcon sx={{ color: '#ef4444', fontSize: 20, mt: 0.1 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.item}
                      </Typography>
                      {item.note && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {item.note}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Main Component ──────────────────────────────────────────

export default function EffieGuide() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      {/* Header */}
      <PageHeader
        icon="emoji_events"
        title="Effie Guide"
        subtitle="Leer van Effie-winnaars en analyseer je campagnes"
        accent={{ color: '#635983', bg: '#dbcdff' }}
      />

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3, borderBottom: 1, borderColor: 'divider',
          '& .Mui-selected': { color: `${GOLD} !important` },
          '& .MuiTabs-indicator': { bgcolor: GOLD },
        }}
      >
        <Tab icon={<SchoolIcon />} iconPosition="start" label="Learnings & Inspiratie" />
        <Tab icon={<AutoFixHighIcon />} iconPosition="start" label="Campagne Analyseren" />
      </Tabs>

      {tab === 0 && <LearnTab />}
      {tab === 1 && <AnalyzeTab />}
    </Box>
  );
}
